import type { SuspenseProps } from 'react'
import { Suspense } from 'react'

import { useDelay } from 'src/shared/hooks/use-delay'

interface DelayedSuspenseProps extends Omit<SuspenseProps, 'fallback'> {
  /**
   * The fallback component to show after the delay
   */
  fallback: NonNullable<SuspenseProps['fallback']>
  /**
   * The delay in milliseconds before showing the fallback component
   * @default 500
   */
  delay?: number
  /**
   * Callback function that is called when the delay is complete
   */
  onDelayComplete?: () => void
}

/**
 * A wrapper around Suspense that only shows the fallback component after a delay.
 * This helps prevent layout shift and flickering for fast network requests.
 */
export const DelayedSuspense = ({ fallback, delay, onDelayComplete, children }: DelayedSuspenseProps) => {
  const show = useDelay({ delay, onComplete: onDelayComplete })

  return <Suspense fallback={show ? fallback : null}>{children}</Suspense>
}
